import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useSelector } from "../../../../store";
import { makeArrayRandom } from "../../../../utils/learner";

export default function WebEquivalentAmountSelection({ question, onAnswer = () => {} }) {
    const questionData = question;
    const learnerState = useSelector((state) => state.learner || {});
    const data = learnerState?.details?.testLevel === "vfh" ? questionData?.vfh : questionData?.acsf4;

    const [choices, setChoices] = useState({});
    const [selectedChoice, setSelectedChoice] = useState(null);
    const [answerBox, setAnswerBox] = useState(data.answerBox);
    const [selectedKeyPrevious, setSelectedKeyPrevious] = useState(null);
    const [availableChoices, setAvailableChoices] = useState(makeArrayRandom(data.choices));

    // every time question change we will set to reset needed to reset
    useEffect(() => {
        setChoices({});
        setSelectedChoice(null);
        setAnswerBox(data.answerBox);
        setSelectedKeyPrevious(null);
        setAvailableChoices(makeArrayRandom(data.choices));
    }, [question,
        data.answerBox,
        data.choices,
    ]);

    const handleChoiceClick = (choice) => {
        setSelectedChoice(choice);
        setSelectedKeyPrevious(null);
    };

    const handleChoiceAssign = (index) => {
        if (choices[index] && !selectedChoice) {
            const choice = data.choices.find((c) => c.value === choices[index].value);
            setSelectedChoice(choice);
            setSelectedKeyPrevious(index);
        }

        if (selectedChoice && (selectedChoice?.value !== choices[index]?.value)) {
            let currentChoices = {...choices};
            const previousChoice = choices[index];

            if (previousChoice) {
                setAvailableChoices((prevChoices) => [
                    ...prevChoices,
                    { ...previousChoice },
                ]);
            }

            // we will check here if coming from previous answer
            if (selectedKeyPrevious !== null) {
                delete currentChoices[selectedKeyPrevious];
                setSelectedKeyPrevious(null);
            }
            // updating answer
            currentChoices = {
                ...currentChoices,
                [index]: {
                    value: selectedChoice.value,
                },
            }
            setChoices(currentChoices);

            if (
                typeof questionData.answer.value === "object" &&
                !Array.isArray(questionData.answer.value)
            ) {
                const answerKeys = Object.keys(questionData.answer.value);

                const formattedAnswer = answerKeys.reduce(
                    (acc, key, keyIndex) => {
                        acc[key] = currentChoices[key]?.value || null;
                        return acc;
                    },
                    {}
                );

                const canProceed = true;
                onAnswer({
                    canProceed,
                    answer: formattedAnswer,
                });
            } else {
                const updatedAnswer = Object.values(currentChoices).map(
                    (choice) => choice.value
                );

                const canProceed = true;
                onAnswer({
                    canProceed,
                    answer: updatedAnswer,
                });
            }

            setAvailableChoices((prevChoices) =>
                prevChoices.filter(
                    (item) => item.value !== selectedChoice.value
                )
            );

            setSelectedChoice(null);
        }
    };

    const handleRemoveChoice = (index) => {
        const removedChoice = choices[index];
        
        if (removedChoice) {
            setChoices((prevChoices) => {
                const newChoices = { ...prevChoices };
                delete newChoices[index];

                if (
                    typeof questionData.answer.value === "object" &&
                    !Array.isArray(questionData.answer.value)
                ) {
                    const answerKeys = Object.keys(
                        questionData.answer.value
                    );

                    let canProceed = false;
                    const formattedAnswer = answerKeys.reduce(
                        (acc, key, keyIndex) => {
                            acc[key] = newChoices[key]?.value || null;
                            if (acc[key] !== null && acc[key] !== "") {
                                canProceed = true;
                            }
                            return acc;
                        },
                        {}
                    );

                    onAnswer({
                        canProceed,
                        answer: formattedAnswer,
                    });
                } else {
                    const updatedAnswer = Object.values(newChoices).map(
                        (choice) => choice.value
                    );

                    // set to false so that next button will be disabled
                    const canProceed = false;
                    onAnswer({
                        canProceed,
                        answer: updatedAnswer,
                    });
                }

                return newChoices;
            });

            setAvailableChoices((prevChoices) => [
                ...prevChoices,
                {
                    value: removedChoice.value,
                },
            ]);
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                width: "950px",
                position: "relative",
                textAlign: "center",
                justifyContent: "center",
            }}
        >
            {/* For Answer */}
            <Box sx={{
                width: "650px",
                borderBottom: "10px solid #0084B4",
                textAlign: "center",
                marginRight: "10px",
            }}>
                <Box sx={{
                    fontWeight: "bold",
                    backgroundColor: "#00B0F0",
                    color: "black",
                    borderBottomWidth: "10px",
                    borderBottomStyle: "solid",
                    borderBottomColor: "#0084B4",
                    padding: "8px",
                    paddingRight: "20px",
                    fontSize: "1.25em",
                    textAlign: "left"
                }}>{data.mainAnswerBoxHeader}</Box>

                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "rgb(242, 242, 242)",
                    padding: "10px",
                    textAlign: "center",
                }}>
                    {answerBox.map((answer, index) => (
                        <Box key={index}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                alignItems: "center",
                                textAlign: "center",
                            }}
                        >
                            <Box sx={{
                                backgroundColor: "rgb(255, 192, 0)",
                                borderTop: "10px solid rgb(185, 138, 23)",
                                width: "200px",
                                height: "60px",
                                padding: "10px",
                                fontWeight: "bold",
                                fontSize: "20px",
                                marginBottom: "10px",
                                textAlign: "center",
                            }}>{answer.headerText}</Box>
                            {answer.children.map((row, rowIndex) => (
                                <Box key={rowIndex}
                                    sx={{
                                        position: "relative",
                                        width: "200px",
                                        height: "60px",
                                        padding: "10px",
                                        fontWeight: "bold",
                                        fontSize: "20px",
                                        marginBottom: "10px",
                                        textAlign: "center",
                                        boxShadow: selectedChoice
                                            ? (row.answerable ? "rgb(199, 0, 199) 0px 0px 0px 3px" : "none")
                                            : "none",
                                        backgroundColor: choices[row.stateKey]
                                            ? "#aad6e7"
                                            : "white",
                                        borderTop: choices[row.stateKey] ?  "none": (row.answerable ? "10px solid rgb(185, 138, 23)" : "10px solid rgb(192, 187, 196)"),
                                    }}
                                    onClick={() => {
                                        if (row.answerable) handleChoiceAssign(row.stateKey)
                                    }}
                                ><span>{row.value}</span>
                                {choices[row.stateKey] ? (
                                    <Box
                                        sx={{
                                            top: "initial",
                                            position: "absolute",
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            lineHeight: "0.8em",
                                            textAlign: "center"
                                        }}
                                    >
                                        <Box sx={{
                                            position: "relative",
                                            height: "52px",
                                            width: "162px",
                                            display: "inline-block",
                                            boxSizing: "content-box",
                                            backgroundColor: "#aad6e7",
                                            padding: "4px",
                                            lineHeight: "0.8em",
                                            textAlign: "center",
                                        }}>
                                            <Box sx={{
                                                display: "inline-flex",
                                                width: "100%",
                                                height: "100%",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                userSelect: "none",
                                                pointerEvents: "none",
                                                fontSize: "16px",
                                                lineHeight: "0.8em",
                                                fontWeight: "bold",
                                                textAlign: "center"
                                            }}>{choices[row.stateKey].value}</Box>
                                        </Box>
                                        {/* Close  */}
                                        <Box
                                            className="selected-close-btn"
                                            sx={{
                                                right: "0px",
                                                top: "0px",
                                                display: "content-box",
                                                lineHeight: "0.8em",
                                                fontWeight: "bold",
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveChoice(row.stateKey);
                                            }}
                                        >X</Box>
                                    </Box>
                                        ) :
                                        <span></span>
                                    }
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* For Choices */}
            <Box sx={{
                position: "relative",
                width: "250px",
                height: "370px",
                padding: "5px",
                border: "1px dashed lightgrey",
                textAlign: "center",
            }}>
                <Box sx={{
                    height: "100%",
                    width: "100%",
                    display: "grid",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "absolute",
                    top: "0",
                    left: "0",
                    textAlign: "center",
                }}>
                    {availableChoices.map((choice, index) => (
                        <Box key={index}
                            sx={{
                                borderBottom: "10px solid rgb(52, 148, 186)",
                                position: "relative",
                                height: "45px",
                                width: "228px",
                                display: "inline-block",
                                boxSizing: "content-box",
                                backgroundColor: '#aad6e7',
                                padding: "4px",
                                lineHeight: "0.8em",
                                textAlign: "center",
                                boxShadow: selectedChoice?.value === choice.value
                                    ? "rgb(199, 0, 199) 0px 0px 0px 3px"
                                    : "none",
                                fontSize: "20px",
                            }}
                            onClick={() => { handleChoiceClick(choice) }}
                            disabled={selectedChoice === choice}
                        >
                            <Box sx={{
                                display: "inline-flex",
                                width: "100%",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "center",
                                userSelect: "none",
                                pointerEvents: "none",
                                fontSize: "16px",
                                lineHeight: "0.8em",
                                textAlign: "center",
                            }}>{choice.value}</Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};